import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Card, CardBody, Label } from 'reactstrap';
import Select from 'react-select';
import { activateAuthLayout } from '../../../store/actions';
import { connect } from 'react-redux';
import { gql } from 'apollo-boost';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { getQuoteBuilderURL } from '../../../helpers/common';
import styled from 'styled-components';
import renderFields from './common/render-caf-fields';
import { useHistory } from 'react-router';

/**
 * graphQL query for all the data needed for customise proposal UI
 * Quote - quote based from the passed quote id in the URL
 **/
const DATA = gql`
  query Data($QuoteId: ID) {
    Quote(QuoteId: $QuoteId) {
      QuoteId
      Description
      WorkflowStep
      Status
      AcceptedQuote
      QuoteSites {
        QuoteSiteId
        Name
      }
    }

    CAFTemplates {
      CAFTemplateId
      Handle
      Name
      Description
      Form
      HasSite
      CAFSectionId
    }

    CAFSections {
      CAFSectionId
      Name
      SortOrder
    }

    QuoteCAFTemplates(QuoteId: $QuoteId) {
      QuoteCAFTemplateId
      Data
      QuoteId
    }
  }
`;

const UPDATE_QUOTE = gql`
  mutation updateQuote($QuoteInput: QuoteInput) {
    updateQuote(input: $QuoteInput) {
      QuoteId
      CompanyName
      WorkflowStep
      AcceptedQuote
      ProposalModified
      CustomerId
    }
  }
`;

const CREATE_QUOTE_CAF_TEMPLATE = gql`
  mutation createQuoteCAFTemplate(
    $QuoteCAFTemplateInput: QuoteCAFTemplateInput
  ) {
    createQuoteCAFTemplate(input: $QuoteCAFTemplateInput) {
      QuoteCAFTemplateId
      Data
      QuoteId
    }
  }
`;
const UPDATE_QUOTE_CAF_TEMPLATE = gql`
  mutation updateQuoteCAFTemplate(
    $QuoteCAFTemplateInput: QuoteCAFTemplateInput
  ) {
    updateQuoteCAFTemplate(input: $QuoteCAFTemplateInput) {
      QuoteCAFTemplateId
      Data
      QuoteId
    }
  }
`;

const Section = styled.div`
  margin-block: 20px;
`;

const SectionHeader = styled.div`
  color: #fff;
  background: #3f86ff;
  padding: 10px 15px;
  font-size: 18px;
  font-weight: 700;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
`;

const Template = styled.div`
  border: 1px solid #ccc;
  user-select: none;
  padding: 15px;
  margin: 0;
  margin-bottom: 10px;
  background: #fff;
`;

const TemplateHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
`;

const StyledSelect = styled(Select)`
  margin-left: auto;
  width: 300px;
`;

const FieldItem = styled.div`
  user-select: none;
  padding: 10px;
  margin: 0;
  margin-top: 10px;
  margin-bottom: 0;
  border: 1px solid #ccc;
  background: #fff;
`;

const FieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FieldLabel = styled(Label)`
  flex: 0 0 20%;
  margin-bottom: 0;
`;

const FieldInput = styled.div`
  flex: 1;
`;

const QuoteBuilderCustomiseCAF = ({ activateAuthLayout, ...props }) => {
  useEffect(() => { activateAuthLayout(); }, [activateAuthLayout]);

  const history = useHistory();

  // initialize states
  const [disableQuote, setDisableQuote] = useState(false);
  const cafTemplateId = useRef(null);
  const userModified = useRef(false);
  const [templateSectionOptions, setTemplateSectionOptions] = useState([]);
  const [quoteCAFTemplates, setQuoteCAFTemplates] = useState({});
  const [toggle, setToggle] = useState({});

  // initialize apollo graphql queries and mutations
  const { data, loading, error } = useQuery(DATA, {
    variables: { QuoteId: props.quoteId },
  });
  const [updateQuote] = useMutation(UPDATE_QUOTE);
  const [createQuoteCAFTemplate] = useMutation(CREATE_QUOTE_CAF_TEMPLATE);
  const [updateQuoteCAFTemplate] = useMutation(UPDATE_QUOTE_CAF_TEMPLATE);
  const addSection = (sectionId, selected) => {
    if (!selected) return;
    userModified.current = true;
    const section = quoteCAFTemplates[sectionId] || { name: '', templates: [] };
    setQuoteCAFTemplates({
      ...quoteCAFTemplates,
      [sectionId]: {
        ...section,
        templates: [
          ...section.templates,
          {
            template_id: selected.value,
            section_handle: selected.handle,
            section_name: selected.label,
            section_description: selected.description || null,
            has_site: selected.hasSite,
            fields: JSON.parse(selected.form),
          },
        ],
      },
    });
  };

  const goToPreviousStep = (e) => {
    e.preventDefault();
    updateQuote({
      variables: {
        QuoteInput: {
          QuoteId: data.Quote.QuoteId,
          WorkflowStep: 'BILLING_CONTACT_CAF',
        },
      },
    }).then(() => {
      history.replace(`/billing-contact-caf/${data.Quote.QuoteId}`);
    });
  };

  const proceedNextStep = (e) => {
    e.preventDefault();
    updateQuote({
      variables: {
        QuoteInput: {
          QuoteId: data.Quote.QuoteId,
          WorkflowStep: 'PREFILL_CAF',
        },
      },
    }).then(() => {
      history.replace(`/prefill-caf/${data.Quote.QuoteId}`);
    });
  };

  // a little function to help us with reordering the result
  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const onDragEnd = (result, sectionId, section_index) => {
    if (!result.destination) return;
    userModified.current = true;

    const section = quoteCAFTemplates[sectionId];
    const items = reorder(
      section.templates[section_index].fields,
      result.source.index,
      result.destination.index
    );

    const cafTemplates = { ...quoteCAFTemplates };
    cafTemplates[sectionId] = {
      ...cafTemplates[sectionId],
      templates: [...cafTemplates[sectionId].templates],
    };
    cafTemplates[sectionId].templates[section_index] = {
      ...cafTemplates[sectionId].templates[section_index],
      fields: items,
    };
    setQuoteCAFTemplates(cafTemplates);
  };

  const onDragSubCategoryEnd = (result, sectionId) => {
    if (!result.destination) return;
    userModified.current = true;

    const section = quoteCAFTemplates[sectionId];
    const items = reorder(
      section.templates,
      result.source.index,
      result.destination.index
    );

    setQuoteCAFTemplates({
      ...quoteCAFTemplates,
      [sectionId]: { ...section, templates: items },
    });
  };

  const deleteSection = (sectionId, template) => {
    userModified.current = true;
    const section = quoteCAFTemplates[sectionId];
    const templates = section.templates.filter(t => t !== template);
    setQuoteCAFTemplates({
      ...quoteCAFTemplates,
      [sectionId]: { ...section, templates },
    });
  };

  const hideSection = (sectionId, template) => {
    userModified.current = true;
    const section = quoteCAFTemplates[sectionId];
    const templates = section.templates.map(t =>
      t === template ? { ...t, hidden: !t.hidden } : t
    );
    setQuoteCAFTemplates({
      ...quoteCAFTemplates,
      [sectionId]: { ...section, templates },
    });
  };

  const hideField = (field, sectionId, section_index) => {
    userModified.current = true;
    const cafTemplates = { ...quoteCAFTemplates };
    const templates = [...cafTemplates[sectionId].templates];
    const fields = templates[section_index].fields.map(f =>
      f === field ? { ...f, hidden: !f.hidden } : f
    );
    templates[section_index] = { ...templates[section_index], fields };
    cafTemplates[sectionId] = { ...cafTemplates[sectionId], templates };
    setQuoteCAFTemplates(cafTemplates);
  };

  const updateTemplateSite = (sectionId, section_index, siteId) => {
    userModified.current = true;
    const cafTemplates = { ...quoteCAFTemplates };
    const templates = [...cafTemplates[sectionId].templates];
    templates[section_index] = { ...templates[section_index], site_id: siteId };
    cafTemplates[sectionId] = { ...cafTemplates[sectionId], templates };
    setQuoteCAFTemplates(cafTemplates);
  };

  const siteOptions = [
    { value: null, label: 'Select site...', isMeta: true },
    ...(data?.Quote?.QuoteSites || []).map(s => ({ value: s.QuoteSiteId, label: s.Name })),
  ];

  const renderSiteDropdown = (sectionId, section_index, template) => {
    if (!template.has_site) return null;
    if (!data.Quote.QuoteSites.length) return null;
    const isReadOnly = !!template.line_id;
    const currentSiteId = template.site_id ?? null;
    return (
      <Select
        options={siteOptions}
        value={siteOptions.find(o => o.value === currentSiteId) || siteOptions[0]}
        onChange={(opt) => updateTemplateSite(sectionId, section_index, opt?.value ?? null)}
        isDisabled={isReadOnly}
        formatOptionLabel={(opt, { context }) =>
          context === 'value'
            ? <><i className="fas fa-building-flag mr-1" /> {opt.label}</>
            : opt.label
        }
        styles={{
          option: (base, { data, isSelected }) => ({ ...base, color: data.isMeta && !isSelected ? '#aaa' : base.color }),
          singleValue: (base, { data }) => ({ ...base, color: data.isMeta ? '#aaa' : base.color }),
        }}
      />
    );
  };

  const renderDragAndDropFields = (sectionId, section_index, template) => {
    return (
      <DragDropContext
        onDragEnd={(result) => onDragEnd(result, sectionId, section_index)}
      >
        <Droppable droppableId="droppable">
          {(provided, snapshot) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
            >
              {template.fields.filter(f => f.name).map((field, index) => (
                <Draggable
                  key={`${field.name}_${section_index}_${index}`}
                  draggableId={`${field.name}_${section_index}_${index}`}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <FieldItem
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      style={{ ...provided.draggableProps.style, opacity: field.hidden ? 0.4 : 1 }}
                    >
                      <FieldRow>
                        <i className="fas fa-grip-vertical" style={{ color: '#bbb' }} {...provided.dragHandleProps} />
                        <FieldLabel>
                          {field.title || <span className="text-muted">No label</span>}{field.validation?.isRequired && <span className="text-danger"> *</span>}
                        </FieldLabel>
                        <FieldInput>{renderFields(field, { readOnly: true })}</FieldInput>
                        <i
                          className={`fas ${field.hidden ? 'fa-eye-slash' : 'fa-eye'}`}
                          style={{ cursor: 'pointer', color: '#bbb' }}
                          onClick={() => hideField(field, sectionId, section_index)}
                        />
                      </FieldRow>
                    </FieldItem>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    );
  };

  useEffect(() => {
    if (error) {
      console.log(error);
    }

    if (data && data.Quote) {
      // disable screen when status is accepted, declined or voided
      const status = data.Quote.Status;
      setDisableQuote(status === 'accepted');

      const options = data.CAFTemplates.map((template) => ({
        value: template.CAFTemplateId,
        handle: template.Handle,
        label: template.Name,
        cafSectionId: template.CAFSectionId || null,
        form: template.Form,
        description: template.Description || null,
        hasSite: template.HasSite || false,
      }));
      setTemplateSectionOptions(options);

      if (data.QuoteCAFTemplates.length > 0) {
        const quoteCAFTemplate = data.QuoteCAFTemplates[0];
        cafTemplateId.current = quoteCAFTemplate.QuoteCAFTemplateId;
        const templates = JSON.parse(quoteCAFTemplate.Data);
        setQuoteCAFTemplates(templates);
      }

      if (!['CUSTOMISE_CAF', 'CUSTOMER_ACCEPTED'].includes(data.Quote.WorkflowStep)) {
        history.replace(getQuoteBuilderURL(data.Quote.WorkflowStep, data.Quote.QuoteId));
        return;
      }
    }
  }, [data, error, history]);

  useEffect(() => {
    if (!userModified.current || !data) return;
    const payload = {
      QuoteId: parseInt(data.Quote.QuoteId, 10),
      Data: JSON.stringify(quoteCAFTemplates),
    };
    if (cafTemplateId.current === null) {
      createQuoteCAFTemplate({
        variables: { QuoteCAFTemplateInput: payload },
        refetchQueries: ['getCurrentQuote'],
      }).then(result => {
        cafTemplateId.current = result.data.createQuoteCAFTemplate.QuoteCAFTemplateId;
      });
    } else {
      updateQuoteCAFTemplate({
        variables: {
          QuoteCAFTemplateInput: {
            ...payload,
            QuoteCAFTemplateId: parseInt(cafTemplateId.current, 10),
          },
        },
        refetchQueries: ['getCurrentQuote'],
      });
    }
  }, [quoteCAFTemplates, data, createQuoteCAFTemplate, updateQuoteCAFTemplate]);

  const cafSections = data ? (data.CAFSections || []) : [];

  return (
    <React.Fragment>
      {disableQuote && <div className="overlay"></div>}
      {
        data && data.Quote && data.CAFTemplates && !loading && (
          <div className="content">
            <div className="container-fluid">
              <div className="page-title-box">
                <Row className="align-items-center">
                  <Col sm="6">
                    <h4 className="page-title">{data.Quote.Description} - Customise CAF</h4>
                  </Col>
                </Row>
              </div>

              <h6>CUSTOMISE CAF</h6>

              <Card>
                <CardBody>
                  <Row>
                    <Col lg={{ size: 8, offset: 2 }}>
                      {cafSections.map((section) => {
                        const sectionId = String(section.CAFSectionId);
                        const sectionData = quoteCAFTemplates[sectionId] || { name: section.Name, templates: [] };
                        const isOpen = toggle[sectionId]?.isOpen !== false;

                        return (
                          <Section key={sectionId}>
                            <SectionHeader
                              onClick={() =>
                                setToggle({
                                  ...toggle,
                                  [sectionId]: { isOpen: !isOpen },
                                })
                              }
                            >
                              {section.Name}
                              {isOpen ? (
                                <i className="fas fa-caret-up"></i>
                              ) : (
                                <i className="fas fa-caret-down"></i>
                              )}
                            </SectionHeader>

                            <DragDropContext
                              onDragEnd={(result) =>
                                onDragSubCategoryEnd(result, sectionId)
                              }
                            >
                              {isOpen && (
                                <Droppable droppableId="droppable-subcategories">
                                  {(provided, snapshot) => (
                                    <div
                                      {...provided.droppableProps}
                                      ref={provided.innerRef}
                                      style={{ background: '#eee', padding: 15 }}
                                    >
                                      {sectionData.templates.length > 0 ? (
                                        sectionData.templates.map(
                                          (template, section_index) => (
                                            <Draggable
                                              key={`${template.section_name}_${section_index}`}
                                              draggableId={`${template.section_name}_${section_index}`}
                                              index={section_index}
                                            >
                                              {(provided, snapshot) => (
                                                <Template
                                                  ref={provided.innerRef}
                                                  {...provided.draggableProps}
                                                  style={{ ...provided.draggableProps.style, opacity: template.hidden ? 0.4 : 1 }}
                                                >
                                                  <TemplateHeader>
                                                    <i className="fas fa-grip-vertical" style={{ color: '#bbb' }} {...provided.dragHandleProps} />
                                                    <h6 className="my-0 mr-auto" style={{ fontSize: 16 }}>{template.section_name}</h6>
                                                    {(template.required || template.line_id) ? (
                                                      <i
                                                        className={`fas ${template.hidden ? 'fa-eye-slash' : 'fa-eye'}`}
                                                        style={{ cursor: 'pointer', color: '#bbb' }}
                                                        onClick={() => hideSection(sectionId, template)}
                                                      />
                                                    ) : (
                                                      <i
                                                        className="fas fa-trash-can"
                                                        style={{ cursor: 'pointer', color: '#bbb' }}
                                                        onClick={() => deleteSection(sectionId, template)}
                                                      />
                                                    )}
                                                  </TemplateHeader>
                                                  {renderSiteDropdown(sectionId, section_index, template)}
                                                  {renderDragAndDropFields(
                                                    sectionId,
                                                    section_index,
                                                    template
                                                  )}
                                                </Template>
                                              )}
                                            </Draggable>
                                          )
                                        )
                                      ) : null}
                                      {provided.placeholder}
                                      {(() => {
                                        const options = templateSectionOptions
                                          .filter(t => t.cafSectionId === section.CAFSectionId || t.cafSectionId === null)
                                          .sort((a, b) => b.cafSectionId - a.cafSectionId);
                                        return options.length > 0 ? (
                                          <StyledSelect
                                            options={options}
                                            placeholder="Add section..."
                                            value={null}
                                            onChange={(val) => addSection(sectionId, val)}
                                          />
                                        ) : null;
                                      })()}
                                    </div>
                                  )}
                                </Droppable>
                              )}
                            </DragDropContext>
                          </Section>
                        );
                      })}
                    </Col>
                  </Row>
                </CardBody>
              </Card>
              <Row>
                <Col lg="12">
                  <Card>
                    <CardBody>
                      <button
                        className="btn-icon btn btn-light btn-lg float-left"
                        onClick={goToPreviousStep}
                      >
                        {' '}
                        <span className="btn-icon-label">
                          <i className="ion ion-md-arrow-back mr-2"></i>
                        </span>{' '}
                        Previous Step
                      </button>
                      <button
                        className="btn-icon btn btn-primary btn-lg float-right"
                        onClick={proceedNextStep}
                      >
                        {' '}
                        <span className="btn-icon-label">
                          <i className="ion ion-md-arrow-forward mr-2"></i>
                        </span>{' '}
                        Next Step
                      </button>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </div>
          </div>
        )
      }
      {
        data && data.Quote === null && !loading && (
          <div className="content">
            <div className="container-fluid">
              <div className="page-title-box">
                <Row className="align-items-center">
                  <Col sm="6">
                    <h4 className="page-title">Error loading this page</h4>
                  </Col>
                </Row>
              </div>

              <Row>
                <Col lg="12">
                  <Card>
                    <CardBody>
                      Quote with id {props.quoteId} does not exist.
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </div>
          </div>
        )
      }
    </React.Fragment>
  );
};

export default connect(null, { activateAuthLayout })(QuoteBuilderCustomiseCAF);
